import { useCallback, useEffect, useState } from "react";
import { analytics } from "./analytics";
import { captureException } from "./sentry";
import { supabase } from "./supabase";

export type Tier = "starter" | "pro";

/** Starter tier constants */
export const STARTER_MONTHLY_LIMIT = 100;

/** Pro tier platform credit limit */
export const PRO_PLATFORM_LIMIT = 2000;

/** Human-readable tier labels */
export const TIER_LABELS: Record<Tier, string> = {
  starter: "Starter",
  pro: "Pro",
};

export interface TierStatus {
  tier: Tier;
  monthly_credits: number;
  credits_used_this_month: number;
  month_start: string;
}

export interface TierCreditResult {
  allowed: boolean;
  tier: Tier;
  use_platform_key: boolean;
  credits_remaining?: number;
  monthly_limit?: number;
  reason?: string;
}

/** Get tier status for the current user (read-only, for UI) */
export async function getTierStatus(): Promise<TierStatus> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user)
    return {
      tier: "starter",
      monthly_credits: 0,
      credits_used_this_month: 0,
      month_start: new Date().toISOString().slice(0, 10),
    };

  const { data, error } = await supabase.rpc("get_tier_status", {
    p_user_id: user.id,
  });

  if (error) throw error;
  const result = typeof data === "string" ? JSON.parse(data) : data;
  return result as TierStatus;
}

/** Check and consume a tier credit (called before LLM requests) */
export async function checkTierCredit(): Promise<TierCreditResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user)
    return {
      allowed: false,
      tier: "starter",
      use_platform_key: true,
      reason: "not_authenticated",
    };

  const { data, error } = await supabase.rpc("check_tier_credit", {
    p_user_id: user.id,
  });

  if (error) throw error;
  const result = typeof data === "string" ? JSON.parse(data) : data;
  return result as TierCreditResult;
}

/** Hook: tier status with refresh capability */
export function useTierCredits() {
  const [status, setStatus] = useState<TierStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const tierStatus = await getTierStatus();
      setStatus(tierStatus);
      // Track starter tier activation (once per device)
      if (tierStatus.tier === "starter") {
        const key = "sm_starter_tier_activated";
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, new Date().toISOString());
          analytics.tier.starterActivated();
        }
      }
    } catch (err) {
      void captureException(err, { tags: { module: "tier-credits" } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const creditsRemaining = status ? status.monthly_credits - status.credits_used_this_month : 0;
  const usesCredits = status != null;

  return {
    status,
    tier: status?.tier ?? "starter",
    isStarter: status?.tier === "starter",
    isPro: status?.tier === "pro",
    monthlyCreditsRemaining: creditsRemaining,
    /** true for users who consume platform credits */
    isDemoUser: usesCredits,
    creditsExhausted: usesCredits && creditsRemaining <= 0,
    loading,
    refresh,
  };
}
